import { useEffect, useRef, useCallback } from 'react';
import { getGrpcResponse } from '../utilities/getGrpcResponse';
import { setPositions, setPositionArray } from '../redux/reducer/positions';
import { setOrders, setOrderArray } from '../redux/reducer/orders';
import { useAppDispatch } from '../redux/app';
import { requestGoogleProtobuf } from '../utilities/request-google-protobuf';
import emitter from '../utilities/emitter';

const { PositionClient } = require('../grpc/generated/position_grpc_web_pb');

const PositionList = (request) => {

  const { server, token } = request;
  const dispatch = useAppDispatch();
  const getPositionList = async () => {
    let position = new PositionClient(server, null, null);
    await position.listPosition(requestGoogleProtobuf, token, (err, response) => {
      if (response === null) {
        setTimeout(() => {
          window.location.reload();
        }, 5000);
        return err;
      } else {
        const data = {
          positions: getGrpcResponse(response)[0],
          orders: getGrpcResponse(response)[1]
        };
        dispatch(setPositions(data.positions));
        dispatch(setPositionArray(data.positions));
        dispatch(setOrders(data.orders));
        dispatch(setOrderArray(data.orders));
      }
    });
  };

  return { getPositionList };
};

/**
 * Hook to automatically refetch position list when connection is restored
 * @param {Object} request - Contains server and token
 * @param {boolean} enabled - Enable/disable auto refetch (default: true)
 */
export const usePositionListRefetch = (request, enabled = true) => {
  const dispatch = useAppDispatch();
  const previousReadyState = useRef(false);
  const isMounted = useRef(true);

  const getPositionList = useCallback(async () => {
    if (!request?.server || !request?.token) {
      console.warn('usePositionListRefetch: Missing server or token');
      return;
    }

    try {
      console.log('Fetching position list...');
      const position = new PositionClient(request.server, null, null);
      
      position.listPosition(requestGoogleProtobuf, request.token, (err, response) => {
        if (!isMounted.current) return;

        if (err || response === null) {
          console.error('Error fetching position list:', err);
          return;
        }

        const data = {
          positions: getGrpcResponse(response)[0],
          orders: getGrpcResponse(response)[1]
        };
        
        dispatch(setPositions(data.positions));
        dispatch(setPositionArray(data.positions));
        dispatch(setOrders(data.orders));
        dispatch(setOrderArray(data.orders));
        console.log('Position list fetched successfully');
      });
    } catch (error) {
      console.error('Exception in getPositionList:', error);
    }
  }, [request?.server, request?.token, dispatch]);

  useEffect(() => {
    if (!enabled) return;

    const handleTradeEventReady = (data) => {
      const isReady = data.isReady;
      
      // Refetch when connection is restored (transition from false to true)
      if (isReady && !previousReadyState.current) {
        console.log('Connection restored - Refetching position list');
        // Small delay to ensure connection is fully stable
        setTimeout(() => {
          getPositionList();
        }, 500);
      }
      
      previousReadyState.current = isReady;
    };

    emitter.instance.on('trade-event-ready', handleTradeEventReady);

    return () => {
      isMounted.current = false;
      emitter.instance.off('trade-event-ready', handleTradeEventReady);
    };
  }, [enabled, getPositionList]);

  return { getPositionList, refetch: getPositionList };
};

export default PositionList;
